// apiService.js — Backend Direct/Bulk Upload ile Uyumlu

// Base URL (env varsa onu, yoksa mevcut origin)
const API_BASE = (process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.host}`)
  .replace(/\/+$/, '');

const looksLikeJSON = (s) => !!s && /^[\s]*[{[]/.test(s);
const contentTypeIsJSON = (ct) => (ct || '').toLowerCase().includes('application/json');

// Yardımcı Fetch Fonksiyonu
async function apiFetch(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  const { method, body, headers: customHeaders, ...rest } = options;

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...customHeaders,
  };

  const config = {
    method: method || (body ? 'POST' : 'GET'),
    headers,
    credentials: 'include',
    ...rest,
  };

  if (typeof body !== 'undefined') {
    config.body = isFormData ? body : JSON.stringify(body);
  }

  const res = await fetch(url, config);

  const ct = res.headers.get('content-type') || '';
  let raw = '';
  try { raw = await res.text(); } catch { raw = ''; }

  let data = null;
  if (raw) {
    if (contentTypeIsJSON(ct) || looksLikeJSON(raw)) {
      try { data = JSON.parse(raw); } catch { }
    }
  }

  if (!res.ok) {
    const msg = (data && (data.message || data.error || data.msg)) || raw || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data ?? raw ?? null;
    throw err;
  }

  if (res.status === 204 || !raw) return null;
  return data ?? raw;
}

// ===========================
// PHOTOS (GÜNCELLENEN KISIM)
// ===========================

// ✅ Controller'daki getAllPhotos ile tam uyumlu (Favorites & Album)
export function getAllPhotos(weddingId, page = 1, limit = 8, album = 'Tümü', showFavoritesOnly = false) {
  const params = {
    page: String(page),
    limit: String(limit),
    _t: String(Date.now()) // Önbelleği atlamak için timestamp
  };

  if (album && album !== 'Tümü') {
    params.album = album;
  }
  if (showFavoritesOnly) {
    params.favorites = 'true';
  }

  const q = new URLSearchParams(params).toString();
  return apiFetch(`/api/photos/wedding/${encodeURIComponent(weddingId)}?${q}`);
}

// ✅ PRESIGNED URL UPLOAD (Yeni Yöntem: Direkt R2'ye)
// Artık her dosya tek tek işlem görür: Sign -> Put -> Confirm
export async function guestUploadPhoto(files, weddingId, album = 'Tümü', caption = '', onProgress) {
  const rawFileList = Array.from(files);
  const fileList = [];

  for (let i = 0; i < rawFileList.length; i++) {
    const file = rawFileList[i];
    const isHeic = file.name?.toLowerCase().endsWith('.heic') || 
                   file.name?.toLowerCase().endsWith('.heif') || 
                   file.type === 'image/heic' || 
                   file.type === 'image/heif';

    if (isHeic) {
      try {
        console.log(`HEIC dosyası algılandı, dönüştürülüyor: ${file.name}`);
        const heic2any = (await import('heic2any')).default;
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.95
        });
        const blobToUse = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
        const convertedFile = new File([blobToUse], newName, {
          type: 'image/jpeg',
          lastModified: file.lastModified || Date.now()
        });
        fileList.push(convertedFile);
        console.log(`HEIC -> JPEG dönüşümü başarılı: ${convertedFile.name}, Yeni boyut: ${(convertedFile.size / 1024 / 1024).toFixed(2)} MB`);
      } catch (err) {
        console.error("Dönüştürme başarısız, orijinal dosya kullanılacak:", err);
        fileList.push(file);
      }
    } else {
      fileList.push(file);
    }
  }

  const totalBytes = fileList.reduce((acc, f) => acc + f.size, 0);
  let loadedBytesMap = new Array(fileList.length).fill(0);

  const updateProgress = () => {
    if (!onProgress) return;
    const totalLoaded = loadedBytesMap.reduce((acc, v) => acc + v, 0);
    const percent = Math.round((totalLoaded * 100) / totalBytes);
    onProgress(percent);
  };

  // Her dosya için upload işlemini başlatan fonksiyon (Tek Parça Presigned PUT - Küçük Dosyalar < 10MB)
  const uploadSingleFile = async (file, index) => {
    let photoId = null;
    try {
      // 1. ADIM: İmzalı URL al
      const signRes = await apiFetch('/api/photos/upload/sign', {
        method: 'POST',
        body: {
          weddingId,
          album,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          caption: caption // Yorum eklendi
        }
      });

      const { uploadUrl, photoId: pId } = signRes;
      photoId = pId;

      // 2. ADIM: Dosyayı R2'ye PUT et (3 Denemeli Dirençli Retry Mekanizması)
      let attempt = 0;
      let success = false;
      let lastError = null;

      while (attempt < 3 && !success) {
        attempt++;
        try {
          await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', uploadUrl, true);
            xhr.timeout = 60000; // 60 saniye zaman aşımı

            if (file.type) {
              xhr.setRequestHeader('Content-Type', file.type);
            }

            xhr.upload.onprogress = (e) => {
              if (e.lengthComputable) {
                loadedBytesMap[index] = e.loaded;
                updateProgress();
              }
            };

            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
              } else {
                reject(new Error(`Upload failed: ${xhr.status}`));
              }
            };

            xhr.onerror = () => reject(new Error(`Ağ hatası (Deneme ${attempt}/3)`));
            xhr.ontimeout = () => reject(new Error(`Yükleme zaman aşımına uğradı (Deneme ${attempt}/3)`));
            xhr.send(file);
          });

          success = true;
        } catch (err) {
          lastError = err;
          console.warn(`[Upload] ${file.name} deneme ${attempt}/3 başarısız:`, err.message);
          if (attempt < 3) {
            await new Promise((r) => setTimeout(r, attempt * 1500)); // 1.5sn, 3sn bekle tekrar dene
          }
        }
      }

      if (!success) {
        throw lastError || new Error('Network error during upload');
      }

      // 3. ADIM: Onayla (Caption'ı confirm adımına dahil ediyoruz - ayrı istek gerekmez)
      await apiFetch('/api/photos/upload/confirm', {
        method: 'POST',
        body: { photoId, caption: caption || undefined }
      });

      return { success: true, photoId };

    } catch (error) {
      console.error(`File ${file.name} upload failed:`, error);
      
      // ✅ HATA RAPORLAMA: Hata anında backend'e bildirim gönder
      try {
        await apiFetch('/api/photos/upload/fail', {
          method: 'POST',
          body: {
            photoId: photoId || null,
            weddingId,
            errorMessage: error.message || String(error),
            errorCode: error.status || 'CLIENT_ERROR'
          }
        });
      } catch (logErr) {
        console.error("Failed to report upload error to backend:", logErr);
      }

      throw error;
    }
  };

  // ✅ YENİ: Parçalı Yükleme (Multipart Upload - Büyük Dosyalar >= 10MB ve Videolar)
  const uploadFileMultipart = async (file, index) => {
    let photoId = null;
    let uploadId = null;
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB per chunk (S3/R2 standardı)
    const totalParts = Math.ceil(file.size / CHUNK_SIZE);
    let loadedChunkBytes = new Array(totalParts).fill(0);

    const updateChunkProgress = () => {
      loadedBytesMap[index] = loadedChunkBytes.reduce((a, b) => a + b, 0);
      updateProgress();
    };

    try {
      // 1. ADIM: Multipart Yüklemeyi Başlat
      const initRes = await apiFetch('/api/photos/upload/multipart/initiate', {
        method: 'POST',
        body: {
          weddingId,
          album,
          fileName: file.name,
          fileType: file.type || 'application/octet-stream',
          fileSize: file.size,
          caption: caption || undefined
        }
      });

      uploadId = initRes.uploadId;
      photoId = initRes.photoId;

      // 2. ADIM: Parçalar için imzalı URL'leri iste
      const partNumbers = Array.from({ length: totalParts }, (_, i) => i + 1);
      const signRes = await apiFetch('/api/photos/upload/multipart/sign-parts', {
        method: 'POST',
        body: { photoId, uploadId, partNumbers }
      });

      const urlMap = new Map(signRes.presignedUrls.map(item => [item.partNumber, item.url]));

      // 3. ADIM: Her parçayı sırayla yükle (Dirençli 3x Retry Mekanizması)
      const completedParts = [];

      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const start = (partNumber - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const uploadUrl = urlMap.get(partNumber);

        let attempt = 0;
        let success = false;
        let etag = null;
        let lastError = null;

        while (attempt < 3 && !success) {
          attempt++;
          try {
            etag = await new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open('PUT', uploadUrl, true);
              xhr.timeout = 60000; // 60 saniye zaman aşımı
              xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

              xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                  loadedChunkBytes[partNumber - 1] = e.loaded;
                  updateChunkProgress();
                }
              };

              xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                  let rawEtag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag');
                  if (!rawEtag) {
                    rawEtag = `"${Math.random().toString(36).substring(2)}"`;
                  }
                  resolve(rawEtag);
                } else {
                  reject(new Error(`Parça ${partNumber} yüklenemedi: HTTP ${xhr.status}`));
                }
              };

              xhr.onerror = () => reject(new Error(`Parça ${partNumber} için ağ hatası (Deneme ${attempt}/3)`));
              xhr.ontimeout = () => reject(new Error(`Parça ${partNumber} zaman aşımına uğradı`));

              xhr.send(chunk);
            });

            success = true;
          } catch (err) {
            lastError = err;
            console.warn(`[Multipart] Parça ${partNumber}/${totalParts} deneme ${attempt}/3 başarısız:`, err.message);
            if (attempt < 3) {
              await new Promise(r => setTimeout(r, attempt * 1000)); // 1sn / 2sn bekle tekrar dene
            }
          }
        }

        if (!success) {
          throw lastError || new Error(`Parça ${partNumber} yüklenemedi.`);
        }

        completedParts.push({
          PartNumber: partNumber,
          ETag: etag
        });
      }

      // 4. ADIM: Multipart Yüklemeyi Tamamla
      await apiFetch('/api/photos/upload/multipart/complete', {
        method: 'POST',
        body: {
          photoId,
          uploadId,
          parts: completedParts,
          caption: caption || undefined
        }
      });

      return { success: true, photoId };

    } catch (error) {
      console.error(`[Multipart] ${file.name} yükleme hatası:`, error);

      if (photoId && uploadId) {
        try {
          await apiFetch('/api/photos/upload/multipart/abort', {
            method: 'POST',
            body: { photoId, uploadId }
          });
        } catch (abortErr) {
          console.error("Multipart abort başarısız:", abortErr);
        }
      }

      try {
        await apiFetch('/api/photos/upload/fail', {
          method: 'POST',
          body: {
            photoId: photoId || null,
            weddingId,
            errorMessage: error.message || String(error),
            errorCode: 'MULTIPART_CLIENT_ERROR'
          }
        });
      } catch (logErr) {}

      throw error;
    }
  };

  const MULTIPART_THRESHOLD = 5 * 1024 * 1024; // 5 MB üzeri dosyalar (S3/R2 standardı parça alt sınırı)
  const isVideoFile = (file) => file.type?.startsWith('video/') || /\.(mov|mp4|webm|ogg|avi|mkv|qt)$/i.test(file.name || '');

  // Tüm dosyaları sırayla (ardışık) yükle
  const results = [];
  for (let i = 0; i < fileList.length; i++) {
    const file = fileList[i];
    const isBigOrVideo = file.size >= MULTIPART_THRESHOLD || isVideoFile(file);

    if (isBigOrVideo) {
      console.log(`[Upload] Parçalı Yükleme (Multipart Upload) seçildi (${(file.size / 1024 / 1024).toFixed(1)} MB): ${file.name}`);
      const res = await uploadFileMultipart(file, i);
      results.push(res);
    } else {
      console.log(`[Upload] Tek Parça Yükleme (Presigned PUT) seçildi (${(file.size / 1024 / 1024).toFixed(1)} MB): ${file.name}`);
      const res = await uploadSingleFile(file, i);
      results.push(res);
    }
  }
  return results;
}

// ⚠️ Legacy Wrapper: Eskiden çoklu yükleme için kullanılan fonksiyonu yeni yapıya yönlendiriyoruz
export async function uploadFiles(files, weddingId, uploadedByEmail, caption = '', album = 'Tümü') {
  return guestUploadPhoto(files, weddingId, album);
}

// ===========================
// DİĞER STANDART FONKSİYONLAR
// ===========================

export function deletePhotoById(photoId) {
  return apiFetch(`/api/photos/${encodeURIComponent(photoId)}`, { method: 'DELETE' });
}

export function bulkDeletePhotos(ids) {
  return apiFetch('/api/photos/delete/bulk', { body: { photoIds: ids } });
}

export function togglePhotoFavorite(photoId) {
  return apiFetch(`/api/photos/${encodeURIComponent(photoId)}/favorite`, { method: 'POST' });
}

// ===========================
// AI FONKSİYONLARI (Yeni Eklendi)
// ===========================
export function applyAIEnhance(photoId) {
  return apiFetch(`/api/photos/${encodeURIComponent(photoId)}/enhance`, { method: 'POST' });
}

export function applyAIArtisticFilter(photoId, filterType) {
  return apiFetch(`/api/photos/${encodeURIComponent(photoId)}/artistic`, { 
    method: 'POST',
    body: { filterType }
  });
}

export function loginOwner(email, password) {
  return apiFetch('/api/auth/login', { body: { email, password } });
}

export function uploadHeroImage(weddingId, file) {
  const fd = new FormData();
  fd.append('heroImage', file);
  return apiFetch(`/api/photos/wedding/${encodeURIComponent(weddingId)}/hero-image`, {
    method: 'POST',
    body: fd,
  });
}

export function verifySession() {
  return apiFetch('/api/auth/verify', { method: 'GET' });
}

export function logout() {
  return apiFetch('/api/auth/logout', { method: 'POST' });
}

export async function requestPasswordReset(email) {
  const response = await apiFetch('/api/auth/forgot-password', { body: { email } });
  if (!response || !response.resetUrl) throw new Error('İşlem başarısız.');
  return response.resetUrl;
}

export function resetPassword(token, password, passwordConfirm) {
  return apiFetch(`/api/auth/reset-password/${encodeURIComponent(token)}`, {
    method: 'PATCH',
    body: { password, passwordConfirm },
  });
}

export function getWeddingByCode(code) {
  return apiFetch(`/api/weddings/by-code/${encodeURIComponent(code)}?_t=${Date.now()}`);
}

export function createDemoWedding(clientEmail, captchaToken) {
  return apiFetch('/api/weddings/demo', { method: 'POST', body: { clientEmail, captchaToken } });
}

export function getCart(cartId) {
  const q = cartId ? `?cartId=${encodeURIComponent(cartId)}` : '';
  return apiFetch(`/api/cart${q}`, { method: 'GET' });
}

export function createOrUpdateCart(cartId, items) {
  return apiFetch('/api/cart', { method: 'POST', body: { cartId, items } });
}

export function removeItemFromCart(cartId, productId) {
  return apiFetch(`/api/cart/${encodeURIComponent(cartId)}/${encodeURIComponent(productId)}`, { method: 'DELETE' });
}

export function getGuestbookEntries(weddingId, isOwner) {
  let endpoint = `/api/guestbook/weddings/${encodeURIComponent(weddingId)}`;
  if (isOwner) endpoint += '?all=true';
  return apiFetch(endpoint);
}

export function addGuestbookEntry(weddingId, data) {
  return apiFetch(`/api/guestbook/weddings/${encodeURIComponent(weddingId)}`, { body: data });
}

export function deleteGuestbookEntry(entryId) {
  return apiFetch(`/api/guestbook/${encodeURIComponent(entryId)}`, { method: 'DELETE' });
}

// ===========================
// ÖDEME FONKSİYONLARI (Lidio Entegrasyonu)
// ===========================

export const startPaymentProcess = async (paymentPayload) => {
  // ✅ apiFetch kullanarak Base URL ve hata yönetimini otomatik hale getiriyoruz
  return apiFetch('/api/payment/start-lidio-payment', {
    method: 'POST',
    body: paymentPayload
  });
};

export function getOrderByLidioId(lidioOrderId) {
  return apiFetch(`/api/order/lidio/${encodeURIComponent(lidioOrderId)}`);
}


export function getAdminAllWeddings() { return apiFetch('/api/admin/weddings'); }
export function updateWeddingQuota(wId, q) { return apiFetch(`/api/admin/weddings/${wId}/quota`, { method: 'PATCH', body: { newQuotaGB: Number(q) } }); }
export function updateWeddingName(wId, name) { return apiFetch(`/api/admin/weddings/${wId}/name`, { method: 'PATCH', body: { name } }); }
export function updateWeddingTheme(wId, t) { return apiFetch(`/api/weddings/${wId}/theme`, { method: 'PATCH', body: { theme: t } }); }
export function updateWeddingAlbums(wId, albums) { return apiFetch(`/api/admin/weddings/${wId}/albums`, { method: 'PUT', body: { albumNames: albums } }); }
export function createWeddingByAdmin(data) { return apiFetch('/api/admin/weddings', { body: data }); }
export function deleteWeddingByAdmin(wId) { return apiFetch(`/api/admin/weddings/${wId}`, { method: 'DELETE' }); }
export function updateWeddingOwnerEmail(wId, email) { return apiFetch(`/api/admin/weddings/${wId}/owner-email`, { method: 'PATCH', body: { ownerEmail: email } }); }
export function setUploadsLocked(wId, l) { return apiFetch(`/api/admin/weddings/${wId}/upload-lock`, { method: 'PATCH', body: { locked: !!l } }); }
export function getAllOrganizations() { return apiFetch('/api/organizations', { method: 'GET' }); }
export function getAdminOrganizationsWithStats() { return apiFetch('/api/admin/organizations', { method: 'GET' }); }
export function createOrganizationAccount(data) { return apiFetch('/api/admin/organizations', { method: 'POST', body: data }); }
export function deleteOrganizationById(orgId) { return apiFetch(`/api/admin/organizations/${encodeURIComponent(orgId)}`, { method: 'DELETE' }); }
export function updateOrganizationCreditsByAdmin(orgId, amount, creditType = 'wedding') { return apiFetch(`/api/admin/organizations/${encodeURIComponent(orgId)}/credits`, { method: 'PATCH', body: { amount, creditType } }); }
export function getAdminPendingOrders() { return apiFetch('/api/admin/pending-orders', { method: 'GET' }); }
export function approveAdminTransferOrder(orderId) { return apiFetch(`/api/admin/orders/${encodeURIComponent(orderId)}/approve`, { method: 'POST' }); }
export function getAdminOrders(filters = {}) {
  const q = new URLSearchParams(filters).toString();
  return apiFetch(`/api/admin/orders?${q}`);
}
export function createAdminManualOrder(data) {
  return apiFetch('/api/admin/orders/manual', { method: 'POST', body: data });
}
export function updateAdminOrderStatus(orderId, orderStatus) {
  return apiFetch(`/api/admin/orders/${encodeURIComponent(orderId)}/status`, { method: 'PATCH', body: { orderStatus } });
}
export function updateAdminOrderPaymentStatus(orderId, paymentStatus) {
  return apiFetch(`/api/admin/orders/${encodeURIComponent(orderId)}/payment-status`, { method: 'PATCH', body: { paymentStatus } });
}
export function deleteAdminOrder(orderId) {
  return apiFetch(`/api/admin/orders/${encodeURIComponent(orderId)}`, { method: 'DELETE' });
}
export function getAdminWeddingLogs(wId, page = 1, limit = 50, level = '') {
  let q = `?page=${page}&limit=${limit}`;
  if (level) q += `&level=${level}`;
  return apiFetch(`/api/admin/logs/${encodeURIComponent(wId)}${q}`, { method: 'GET' });
}

// Legacy (Artık kullanılmıyor)
export function signUploads() { return Promise.resolve({}); }
export function confirmUploads() { return Promise.resolve({}); }

export function getAdminAllBlogs() {
  return apiFetch('/api/admin/blogs');
}

export function createBlogPost(data) {
  return apiFetch('/api/admin/blogs', {
    method: 'POST',
    body: data
  });
}

export function deleteBlogPost(blogId) {
  return apiFetch(`/api/admin/blogs/${encodeURIComponent(blogId)}`, {
    method: 'DELETE'
  });
}

// ===========================
// ORGANİZASYON YÖNETİMİ (MÜŞTERİ PANELİ)
// ===========================
export function createOrganizationOrder(data) { return apiFetch('/api/organizations/create-order', { method: 'POST', body: data }); }
export function getOrganizationInfo() { return apiFetch('/api/organizations/info'); }
export function getMyWeddings() { return apiFetch('/api/organizations/my-weddings'); }
export function createWeddingByOrganization(data) { return apiFetch('/api/organizations/create-wedding', { method: 'POST', body: data }); }
export function updateWeddingByOrg(id, updates) { return apiFetch(`/api/organizations/weddings/${id}`, { method: 'PATCH', body: updates }); }

// ===========================
// KUPON FONKSİYONLARI 
// ===========================
export function validateCoupon(code) { return apiFetch('/api/coupons/validate', { method: 'POST', body: { code } }); }
export function getAdminAllCoupons() { return apiFetch('/api/admin/coupons'); }
export function createCouponByAdmin(data) { return apiFetch('/api/admin/coupons', { method: 'POST', body: data }); }
export function deleteCouponByAdmin(id) { return apiFetch(`/api/admin/coupons/${id}`, { method: 'DELETE' }); }
export function toggleCouponByAdmin(id) { return apiFetch(`/api/admin/coupons/${id}/toggle`, { method: 'PATCH' }); }

export default {
  loginOwner,
  verifySession,
  logout,
  getWeddingByCode,
  createDemoWedding,
  getAllPhotos,
  uploadFiles,
  guestUploadPhoto,
  deletePhotoById,
  bulkDeletePhotos,
  getAdminAllWeddings,
  updateWeddingQuota,
  updateWeddingName,
  updateWeddingTheme,
  updateWeddingAlbums,
  createWeddingByAdmin,
  updateWeddingOwnerEmail,
  requestPasswordReset,
  resetPassword,
  setUploadsLocked,
  getGuestbookEntries,
  addGuestbookEntry,
  deleteGuestbookEntry,
  deleteWeddingByAdmin,
  getCart,
  createOrUpdateCart,
  removeItemFromCart,
  getAllOrganizations,
  togglePhotoFavorite,
  uploadHeroImage,
  signUploads,
  confirmUploads,
  startPaymentProcess,
  getAdminAllBlogs,
  createBlogPost,
  deleteBlogPost,
  validateCoupon,
  getAdminAllCoupons,
  createCouponByAdmin,
  deleteCouponByAdmin,
  toggleCouponByAdmin,
  getAdminOrganizationsWithStats,
  createOrganizationAccount,
  deleteOrganizationById,
  updateOrganizationCreditsByAdmin,
  getAdminPendingOrders,
  approveAdminTransferOrder,
  getAdminOrders,
  createAdminManualOrder,
  updateAdminOrderStatus,
  updateAdminOrderPaymentStatus,
  deleteAdminOrder,
  getOrganizationInfo,
  getMyWeddings,
  createWeddingByOrganization,
  updateWeddingByOrg,
  createOrganizationOrder,
  getAdminWeddingLogs,
  applyAIEnhance,
  applyAIArtisticFilter
};